import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api';

const SecuritySettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState({});

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    if (!password) return 0;
    
    let strength = 0;
    
    // Length
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 15;
    
    // Contains lowercase
    if (/[a-z]/.test(password)) strength += 15;
    
    // Contains uppercase
    if (/[A-Z]/.test(password)) strength += 15;
    
    // Contains numbers
    if (/\d/.test(password)) strength += 15;
    
    // Contains special characters
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) strength += 15;
    
    return Math.min(strength, 100);
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength < 40) return 'error';
    if (strength < 70) return 'warning';
    return 'success';
  };

  const getPasswordStrengthLabel = (strength) => {
    if (strength < 40) return t('security.passwordWeak', 'Weak');
    if (strength < 70) return t('security.passwordMedium', 'Medium');
    return t('security.passwordStrong', 'Strong');
  };

  const passwordStrength = calculatePasswordStrength(formData.newPassword);

  // Password requirements
  const passwordRequirements = [
    {
      label: t('security.reqLength', 'At least 8 characters'),
      met: formData.newPassword.length >= 8
    },
    {
      label: t('security.reqUppercase', 'Contains uppercase letter'),
      met: /[A-Z]/.test(formData.newPassword)
    },
    {
      label: t('security.reqLowercase', 'Contains lowercase letter'),
      met: /[a-z]/.test(formData.newPassword)
    },
    {
      label: t('security.reqNumber', 'Contains number'),
      met: /\d/.test(formData.newPassword)
    },
    {
      label: t('security.reqSpecial', 'Contains special character'),
      met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.newPassword)
    }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.currentPassword) {
      newErrors.currentPassword = t('security.currentPasswordRequired', 'Current password is required');
    }
    
    if (!formData.newPassword) {
      newErrors.newPassword = t('security.newPasswordRequired', 'New password is required');
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = t('security.passwordTooShort', 'Password must be at least 8 characters');
    } else if (passwordStrength < 40) {
      newErrors.newPassword = t('security.passwordTooWeak', 'Password is too weak');
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('security.confirmPasswordRequired', 'Please confirm your password');
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('security.passwordsDoNotMatch', 'Passwords do not match');
    }
    
    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = t('security.samePassword', 'New password must be different from current password');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/auth/change-password`,
        {
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSnackbar({
        open: true,
        message: t('security.passwordChangeSuccess', 'Password changed successfully'),
        severity: 'success'
      });

      // Clear form
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Navigate back to settings after a delay
      setTimeout(() => {
        navigate('/settings');
      }, 2000);

    } catch (error) {
      console.error('Error changing password:', error);
      
      let errorMessage = t('security.passwordChangeError', 'Failed to change password');
      
      if (error.response?.status === 401) {
        errorMessage = t('security.incorrectCurrentPassword', 'Current password is incorrect');
        setErrors({ currentPassword: errorMessage });
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/settings')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {t('security.title', 'Security Settings')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('security.subtitle', 'Change your password and security settings')}
          </Typography>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <LockIcon color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('security.changePassword', 'Change Password')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('security.changePasswordDesc', 'Choose a strong password to keep your account secure')}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <form onSubmit={handleSubmit}>
          {/* Current Password */}
          <TextField
            fullWidth
            type={showPasswords.current ? 'text' : 'password'}
            label={t('security.currentPassword', 'Current Password')}
            value={formData.currentPassword}
            onChange={handleChange('currentPassword')}
            error={!!errors.currentPassword}
            helperText={errors.currentPassword}
            disabled={isSubmitting}
            required
            sx={{ mb: 3 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility('current')}
                    edge="end"
                  >
                    {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {/* New Password */}
          <TextField
            fullWidth
            type={showPasswords.new ? 'text' : 'password'}
            label={t('security.newPassword', 'New Password')}
            value={formData.newPassword}
            onChange={handleChange('newPassword')}
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            disabled={isSubmitting}
            required
            sx={{ mb: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility('new')}
                    edge="end"
                  >
                    {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {/* Password Strength Indicator */}
          {formData.newPassword && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('security.passwordStrength', 'Password Strength')}:
                </Typography>
                <Typography 
                  variant="caption" 
                  color={`${getPasswordStrengthColor(passwordStrength)}.main`}
                  sx={{ fontWeight: 600 }}
                >
                  {getPasswordStrengthLabel(passwordStrength)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={passwordStrength}
                color={getPasswordStrengthColor(passwordStrength)}
                sx={{ height: 6, borderRadius: 3 }}
              />

              {/* Password Requirements */}
              <List dense sx={{ mt: 2, bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
                {passwordRequirements.map((req, index) => (
                  <ListItem key={index} dense>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {req.met ? (
                        <CheckIcon fontSize="small" color="success" />
                      ) : (
                        <CloseIcon fontSize="small" color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={req.label}
                      primaryTypographyProps={{
                        variant: 'caption',
                        color: req.met ? 'text.primary' : 'text.secondary'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Confirm Password */}
          <TextField
            fullWidth
            type={showPasswords.confirm ? 'text' : 'password'}
            label={t('security.confirmPassword', 'Confirm New Password')}
            value={formData.confirmPassword}
            onChange={handleChange('confirmPassword')}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            disabled={isSubmitting}
            required
            sx={{ mb: 3 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility('confirm')}
                    edge="end"
                  >
                    {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {/* Security Notice */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {t('security.notice', 'For security reasons, you will need to log in again after changing your password.')}
            </Typography>
          </Alert>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/settings')}
              disabled={isSubmitting}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <LockIcon />}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('common.changing', 'Changing...') : t('security.changePassword', 'Change Password')}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SecuritySettings;
